/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import {
  getEmployeeTasks,
  createEmployeeTask,
  updateEmployeeTask,
  deleteEmployeeTask,
  getEmployeeTaskReports,
  deleteEmployeeReportExpense,
  getEmployees,
  getProjects,
  getUsers,
  getReceipt,
  getTaskWorkTypes,
  resolveRate,
  generateTaskLink,
  sendTaskLink
} from '../../services/api';
import { TaskLinkDialog } from '../../components/TaskLinkDialog';
import { formatAmount } from '../../lib/workStatus';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Copy, Eye, Trash2, Link2 } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'assigned', label: 'Piešķirts' },
  { value: 'in_progress', label: 'Procesā' },
  { value: 'submitted', label: 'Iesniegts' },
  { value: 'completed', label: 'Pabeigts' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Zema' },
  { value: 'medium', label: 'Vidēja' },
  { value: 'high', label: 'Augsta' }
];

const emptyForm = {
  title: '',
  description: '',
  due_date: '',
  start_date: '',
  property_name: '',
  property_address: '',
  client_id: '',
  project_id: '',
  priority: 'medium',
  status: 'assigned',
  employee_id: '',
  work_type: '',
  unit: '',
  planned_amount: '',
  rate: '',
  admin_notes: ''
};

const EmployeeTasksManagement = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [filters, setFilters] = useState({ employee_id: 'all', status: 'all', priority: 'all' });
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [linkTask, setLinkTask] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkJustCreated, setLinkJustCreated] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [taskData, employeeData, projectData, userData, workTypeData] = await Promise.all([
        getEmployeeTasks(filters.employee_id !== 'all' || filters.status !== 'all' || filters.priority !== 'all'
          ? {
              employee_id: filters.employee_id !== 'all' ? filters.employee_id : undefined,
              status: filters.status !== 'all' ? filters.status : undefined,
              priority: filters.priority !== 'all' ? filters.priority : undefined
            }
          : {}),
        getEmployees(),
        getProjects(),
        getUsers(),
        getTaskWorkTypes()
      ]);
      setTasks(taskData || []);
      setEmployees(employeeData || []);
      setProjects(projectData || []);
      setClients((userData || []).filter((user) => user.role === 'client'));
      setWorkTypes(workTypeData || []);
    } catch (error) {
      toast.error(t('portalUi.admin.employeeTasks.toastLoadError', 'Neizdevās ielādēt darbinieku uzdevumus'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const focusId = searchParams.get('task');
    if (!focusId || loading) return;
    const found = tasks.find((task) => task.id === focusId);
    if (found) {
      openModal(found);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, tasks, loading]);

  const openModal = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title || '',
        description: task.description || '',
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        start_date: task.start_date ? task.start_date.split('T')[0] : '',
        property_name: task.property_name || '',
        property_address: task.property_address || '',
        client_id: task.client_id || '',
        project_id: task.project_id || '',
        priority: task.priority || 'medium',
        status: task.status || 'assigned',
        employee_id: task.employee_id || '',
        work_type: task.work_type || '',
        unit: task.unit || '',
        planned_amount: task.planned_amount ?? '',
        rate: task.rate ?? '',
        admin_notes: task.admin_notes || ''
      });
    } else {
      setEditingTask(null);
      setFormData({ ...emptyForm });
    }
    setIsModalOpen(true);
  };

  const handleWorkTypeChange = (value) => {
    const catalog = workTypes.find((item) => item.key === value);
    setFormData((prev) => ({
      ...prev,
      work_type: value,
      unit: catalog?.unit || prev.unit,
      title: prev.title || catalog?.label || ''
    }));
  };

  const [autoRate, setAutoRate] = useState(null);
  useEffect(() => {
    if (!isModalOpen || !formData.work_type) { setAutoRate(null); return; }
    let cancelled = false;
    resolveRate({ work_type: formData.work_type, employee_id: formData.employee_id || undefined, date: formData.start_date || undefined })
      .then((data) => { if (!cancelled) setAutoRate(data); })
      .catch(() => { if (!cancelled) setAutoRate(null); });
    return () => { cancelled = true; };
  }, [isModalOpen, formData.work_type, formData.employee_id, formData.start_date]);

  const openLink = async (task, justCreated = false) => {
    try {
      const data = await generateTaskLink(task.id);
      setLinkUrl(`${window.location.origin}${data.path}`);
      setLinkJustCreated(justCreated);
      setLinkTask(task);
    } catch (error) {
      toast.error('Neizdevās ģenerēt saiti');
    }
  };

  const linkEmail = linkTask?.employee_email || employees.find((employee) => employee.id === linkTask?.employee_id)?.email || '';

  const submitTask = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.employee_id) {
      toast.error(t('portalUi.admin.employeeTasks.toastValidation', 'Aizpildiet obligātos laukus'));
      return;
    }

    try {
      const payload = {
        ...formData,
        due_date: formData.due_date || null,
        start_date: formData.start_date || null,
        client_id: formData.client_id || null,
        project_id: formData.project_id || null,
        work_type: formData.work_type || null,
        unit: formData.unit || null,
        planned_amount: formData.planned_amount === '' ? null : Number(formData.planned_amount),
        rate: formData.rate === '' ? null : Number(formData.rate),
        admin_notes: formData.admin_notes || null
      };
      if (editingTask) {
        await updateEmployeeTask(editingTask.id, payload);
        toast.success(t('portalUi.admin.employeeTasks.toastUpdated', 'Uzdevums atjaunināts'));
        setIsModalOpen(false);
      } else {
        const created = await createEmployeeTask(payload);
        toast.success(t('portalUi.admin.employeeTasks.toastCreated', 'Uzdevums pievienots'));
        setIsModalOpen(false);
        openLink(created, true);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portalUi.admin.employeeTasks.toastError', 'Kļūda saglabājot uzdevumu'));
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm(t('portalUi.admin.employeeTasks.confirmDelete', 'Vai tiešām dzēst uzdevumu?'))) return;
    try {
      await deleteEmployeeTask(taskId);
      toast.success(t('portalUi.admin.employeeTasks.toastDeleted', 'Uzdevums dzēsts'));
      fetchData();
    } catch (error) {
      toast.error(t('portalUi.admin.employeeTasks.toastDeleteError', 'Neizdevās dzēst uzdevumu'));
    }
  };

  const handleProjectChange = (projectId) => {
    const project = projects.find((item) => item.id === projectId);
    setFormData((prev) => ({
      ...prev,
      project_id: projectId,
      client_id: project ? project.client_id || prev.client_id : prev.client_id
    }));
  };

  const openReports = async (task) => {
    try {
      setSelectedTask(task);
      const data = await getEmployeeTaskReports(task.id);
      setReports(data || []);
      setIsReportsOpen(true);
    } catch (error) {
      toast.error(t('portalUi.admin.employeeTasks.toastReportsError', 'Neizdevās ielādēt atskaites'));
    }
  };

  const handleReceiptView = async (receiptId) => {
    try {
      const blob = await getReceipt(receiptId);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error(t('portalUi.admin.employeeTasks.toastReceiptOpenError', 'Neizdevās atvērt čeku'));
    }
  };

  const handleExpenseDelete = async (reportId, expenseId) => {
    if (!window.confirm(t('portalUi.admin.employeeTasks.confirmExpenseDelete', 'Vai dzēst šo izdevumu?'))) return;
    try {
      const updated = await deleteEmployeeReportExpense(reportId, expenseId);
      setReports((prev) => prev.map((report) => (report.id === updated.id ? updated : report)));
      toast.success(t('portalUi.admin.employeeTasks.toastExpenseDeleted', 'Izdevums dzēsts'));
    } catch (error) {
      toast.error(t('portalUi.admin.employeeTasks.toastExpenseDeleteError', 'Neizdevās dzēst izdevumu'));
    }
  };

  const statusLabel = (value) => STATUS_OPTIONS.find((option) => option.value === value)?.label || value;
  const priorityLabel = (value) => PRIORITY_OPTIONS.find((option) => option.value === value)?.label || value;

  const tasksWithLabels = useMemo(() => (
    tasks.map((task) => ({
      ...task,
      statusLabel: statusLabel(task.status),
      priorityLabel: priorityLabel(task.priority)
    }))
  ), [tasks]);

  return (
    <div className="space-y-6" data-testid="admin-employee-tasks-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F3D2B]" data-testid="admin-employee-tasks-title">
            {t('portalUi.admin.employeeTasks.title', 'Darbinieku uzdevumi')}
          </h1>
          <p className="text-[#2E2E2E]/70" data-testid="admin-employee-tasks-subtitle">
            {t('portalUi.admin.employeeTasks.subtitle', 'Piešķiriet uzdevumus un pārskatiet atskaites')}
          </p>
        </div>
        <Button onClick={() => openModal()} data-testid="admin-employee-tasks-add">
          {t('portalUi.admin.employeeTasks.addTask', 'Pievienot uzdevumu')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-employee-tasks-filters">
        <Card>
          <CardContent className="pt-6 space-y-2">
            <Label data-testid="admin-employee-tasks-filter-employee-label">{t('portalUi.admin.employeeTasks.filters.employee', 'Darbinieks')}</Label>
            <Select value={filters.employee_id} onValueChange={(value) => setFilters((prev) => ({ ...prev, employee_id: value }))}>
              <SelectTrigger data-testid="admin-employee-tasks-filter-employee">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="admin-employee-tasks-filter-employee-all">{t('portalUi.admin.employeeTasks.filters.all', 'Visi')}</SelectItem>
                {employees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id} data-testid={`admin-employee-tasks-filter-employee-${employee.id}`}>
                    {employee.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 space-y-2">
            <Label data-testid="admin-employee-tasks-filter-status-label">{t('portalUi.admin.employeeTasks.filters.status', 'Statuss')}</Label>
            <Select value={filters.status} onValueChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}>
              <SelectTrigger data-testid="admin-employee-tasks-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="admin-employee-tasks-filter-status-all">{t('portalUi.admin.employeeTasks.filters.all', 'Visi')}</SelectItem>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} data-testid={`admin-employee-tasks-filter-status-${option.value}`}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 space-y-2">
            <Label data-testid="admin-employee-tasks-filter-priority-label">{t('portalUi.admin.employeeTasks.filters.priority', 'Prioritāte')}</Label>
            <Select value={filters.priority} onValueChange={(value) => setFilters((prev) => ({ ...prev, priority: value }))}>
              <SelectTrigger data-testid="admin-employee-tasks-filter-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="admin-employee-tasks-filter-priority-all">{t('portalUi.admin.employeeTasks.filters.all', 'Visi')}</SelectItem>
                {PRIORITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} data-testid={`admin-employee-tasks-filter-priority-${option.value}`}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48" data-testid="admin-employee-tasks-loading">
          <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Card className="border-0 shadow-sm" data-testid="admin-employee-tasks-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#f8faf8]">
                  <tr>
                    <th className="text-left px-6 py-4">{t('portalUi.admin.employeeTasks.table.title', 'Uzdevums')}</th>
                    <th className="text-left px-6 py-4">{t('portalUi.admin.employeeTasks.table.employee', 'Darbinieks')}</th>
                    <th className="text-left px-6 py-4">{t('portalUi.admin.employeeTasks.table.progress', 'Apjoms')}</th>
                    <th className="text-left px-6 py-4">{t('portalUi.admin.employeeTasks.table.dueDate', 'Termiņš')}</th>
                    <th className="text-left px-6 py-4">{t('portalUi.admin.employeeTasks.table.status', 'Statuss')}</th>
                    <th className="text-left px-6 py-4">{t('portalUi.admin.employeeTasks.table.priority', 'Prioritāte')}</th>
                    <th className="text-right px-6 py-4">{t('portalUi.admin.employeeTasks.table.actions', 'Darbības')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tasksWithLabels.map((task) => (
                    <tr key={task.id} className="hover:bg-[#f8faf8] transition-colors" data-testid={`admin-employee-task-row-${task.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#1F3D2B]" data-testid={`admin-employee-task-title-${task.id}`}>{task.title}</div>
                        <div className="text-xs text-[#2E2E2E]/60" data-testid={`admin-employee-task-project-${task.id}`}>{task.project_name || task.client_name || '-'}{task.work_type_label ? ` · ${task.work_type_label}` : ''}{task.effective_rate != null ? <span data-testid={`admin-employee-task-rate-${task.id}`}> · {Number(task.effective_rate).toFixed(2)} €/{task.unit || ''} <span className="text-[#2E2E2E]/40">({task.rate_source_label})</span></span> : ''}</div>
                      </td>
                      <td className="px-6 py-4" data-testid={`admin-employee-task-employee-${task.id}`}>{task.employee_name || '-'}</td>
                      <td className="px-6 py-4" data-testid={`admin-employee-task-progress-${task.id}`}>
                        {task.planned_amount ? (
                          <div className="min-w-[120px]">
                            <div className="text-xs mb-1">{formatAmount(task.reported_amount, task.unit)} / {formatAmount(task.planned_amount, task.unit)}</div>
                            <div className="h-1.5 rounded-full bg-gray-200"><div className="h-1.5 rounded-full bg-[#1F3D2B]" style={{ width: `${Math.min(100, (task.reported_amount / task.planned_amount) * 100)}%` }} /></div>
                          </div>
                        ) : (
                          <span className="text-xs text-[#2E2E2E]/60">{task.entry_count ? formatAmount(task.reported_amount, task.unit) : '-'}</span>
                        )}
                      </td>
                      <td className="px-6 py-4" data-testid={`admin-employee-task-due-${task.id}`}>{task.due_date ? new Date(task.due_date).toLocaleDateString('lv-LV') : '-'}</td>
                      <td className="px-6 py-4" data-testid={`admin-employee-task-status-${task.id}`}>
                        <Badge className="bg-blue-100 text-blue-700">{task.statusLabel}</Badge>
                      </td>
                      <td className="px-6 py-4" data-testid={`admin-employee-task-priority-${task.id}`}>
                        <Badge className="bg-[#1F3D2B]/10 text-[#1F3D2B]">{task.priorityLabel}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => openLink(task)} data-testid={`admin-employee-task-link-${task.id}`}>
                            <Link2 className="w-4 h-4 mr-1" />
                            {t('portalUi.admin.employeeTasks.generateLink', 'Saite')}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openModal(task)} data-testid={`admin-employee-task-edit-${task.id}`}>
                            {t('portalUi.admin.employeeTasks.edit', 'Rediģēt')}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openReports(task)} data-testid={`admin-employee-task-reports-btn-${task.id}`}>
                            {t('portalUi.admin.employeeTasks.viewReports', 'Čeki')} ({task.report_count || 0})
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(task.id)} data-testid={`admin-employee-task-delete-${task.id}`}>
                            {t('portalUi.admin.employeeTasks.delete', 'Dzēst')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {tasksWithLabels.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-6 py-6 text-center text-[#2E2E2E]/60" data-testid="admin-employee-tasks-empty">
                        {t('portalUi.admin.employeeTasks.empty', 'Nav piešķirtu uzdevumu')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="admin-employee-task-modal-title">
              {editingTask ? t('portalUi.admin.employeeTasks.editTitle', 'Rediģēt uzdevumu') : t('portalUi.admin.employeeTasks.addTitle', 'Jauns uzdevums')}
            </DialogTitle>
            <DialogDescription className="sr-only" data-testid="admin-employee-task-modal-desc">
              {t('portalUi.admin.employeeTasks.modalDescription', 'Aizpildiet uzdevuma informāciju')}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={submitTask}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label data-testid="admin-employee-task-worktype-label">{t('portalUi.admin.employeeTasks.form.workType', 'Darba veids')}</Label>
                <Select value={formData.work_type || 'none'} onValueChange={(value) => handleWorkTypeChange(value === 'none' ? '' : value)}>
                  <SelectTrigger data-testid="admin-employee-task-worktype">
                    <SelectValue placeholder={t('portalUi.admin.employeeTasks.form.workTypePlaceholder', 'Izvēlieties darba veidu')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="admin-employee-task-worktype-none">{t('portalUi.admin.employeeTasks.form.clientNone', 'Nav')}</SelectItem>
                    {workTypes.map((item) => (
                      <SelectItem key={item.key} value={item.key} data-testid={`admin-employee-task-worktype-${item.key}`}>
                        {item.label} ({item.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label data-testid="admin-employee-task-title-label">{t('portalUi.admin.employeeTasks.form.title', 'Nosaukums')}</Label>
                <Input value={formData.title} onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))} data-testid="admin-employee-task-title" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label data-testid="admin-employee-task-planned-label">{t('portalUi.admin.employeeTasks.form.plannedAmount', 'Plānotais apjoms')}</Label>
                  <Input type="number" step="0.01" value={formData.planned_amount} onChange={(e) => setFormData((prev) => ({ ...prev, planned_amount: e.target.value }))} data-testid="admin-employee-task-planned" />
                </div>
                <div className="space-y-2">
                  <Label data-testid="admin-employee-task-unit-label">{t('portalUi.admin.employeeTasks.form.unit', 'Mērvienība')}</Label>
                  <Input value={formData.unit} onChange={(e) => setFormData((prev) => ({ ...prev, unit: e.target.value }))} placeholder="ha" data-testid="admin-employee-task-unit" />
                </div>
                <div className="space-y-2">
                  <Label data-testid="admin-employee-task-rate-label">{t('portalUi.admin.employeeTasks.form.rate', 'Likme (€)')}</Label>
                  <Input type="number" step="0.01" value={formData.rate} onChange={(e) => setFormData((prev) => ({ ...prev, rate: e.target.value }))} placeholder={autoRate ? String(autoRate.rate) : 'auto'} data-testid="admin-employee-task-rate" />
                </div>
              </div>
              {formData.work_type && (
                <div className="md:col-span-2 rounded-lg bg-[#f8faf8] px-3 py-2 text-xs text-[#2E2E2E]/70" data-testid="admin-employee-task-rate-hint">
                  {formData.rate !== '' && formData.rate !== null
                    ? <>Uzdevumam būs <strong>sava likme {Number(formData.rate).toFixed(2)} €</strong> (aizstāj {autoRate ? `${autoRate.source_label} ${autoRate.rate.toFixed(2)} €` : 'automātisko likmi'}). Atstājiet lauku tukšu, lai izmantotu automātisko.</>
                    : autoRate
                      ? <>Automātiski: <strong>{autoRate.rate.toFixed(2)} € / {autoRate.unit}</strong> ({autoRate.source_label}{autoRate.source === 'employee' && autoRate.standard_rate != null ? `, standarta ${autoRate.standard_rate.toFixed(2)} €` : ''}). Likme tiek fiksēta katram darba ierakstam pēc tā datuma.</>
                      : 'Likme tiks noteikta automātiski no darba veidu kataloga.'}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label data-testid="admin-employee-task-start-label">{t('portalUi.admin.employeeTasks.form.startDate', 'Sākuma datums')}</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))} data-testid="admin-employee-task-start" />
                </div>
                <div className="space-y-2">
                  <Label data-testid="admin-employee-task-due-label">{t('portalUi.admin.employeeTasks.form.dueDate', 'Termiņš')}</Label>
                  <Input type="date" value={formData.due_date} onChange={(e) => setFormData((prev) => ({ ...prev, due_date: e.target.value }))} data-testid="admin-employee-task-due" />
                </div>
              </div>
              <div className="space-y-2">
                <Label data-testid="admin-employee-task-employee-label">{t('portalUi.admin.employeeTasks.form.employee', 'Darbinieks')}</Label>
                <Select value={formData.employee_id} onValueChange={(value) => setFormData((prev) => ({ ...prev, employee_id: value }))}>
                  <SelectTrigger data-testid="admin-employee-task-employee">
                    <SelectValue placeholder={t('portalUi.admin.employeeTasks.form.employeePlaceholder', 'Izvēlieties darbinieku')} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id} data-testid={`admin-employee-task-employee-${employee.id}`}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label data-testid="admin-employee-task-priority-label">{t('portalUi.admin.employeeTasks.form.priority', 'Prioritāte')}</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData((prev) => ({ ...prev, priority: value }))}>
                  <SelectTrigger data-testid="admin-employee-task-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value} data-testid={`admin-employee-task-priority-${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label data-testid="admin-employee-task-client-label">{t('portalUi.admin.employeeTasks.form.client', 'Klients')}</Label>
                <Select value={formData.client_id || 'none'} onValueChange={(value) => setFormData((prev) => ({ ...prev, client_id: value === 'none' ? '' : value }))}>
                  <SelectTrigger data-testid="admin-employee-task-client">
                    <SelectValue placeholder={t('portalUi.admin.employeeTasks.form.clientPlaceholder', 'Izvēlieties klientu')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="admin-employee-task-client-none">{t('portalUi.admin.employeeTasks.form.clientNone', 'Nav')}</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id} data-testid={`admin-employee-task-client-${client.id}`}>
                        {client.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label data-testid="admin-employee-task-project-label">{t('portalUi.admin.employeeTasks.form.project', 'Projekts')}</Label>
                <Select value={formData.project_id || 'none'} onValueChange={(value) => handleProjectChange(value === 'none' ? '' : value)}>
                  <SelectTrigger data-testid="admin-employee-task-project">
                    <SelectValue placeholder={t('portalUi.admin.employeeTasks.form.projectPlaceholder', 'Izvēlieties projektu')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="admin-employee-task-project-none">{t('portalUi.admin.employeeTasks.form.projectNone', 'Nav')}</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id} data-testid={`admin-employee-task-project-${project.id}`}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label data-testid="admin-employee-task-property-label">{t('portalUi.admin.employeeTasks.form.property', 'Īpašums')}</Label>
                <Input value={formData.property_name} onChange={(e) => setFormData((prev) => ({ ...prev, property_name: e.target.value }))} data-testid="admin-employee-task-property" />
              </div>
              <div className="space-y-2">
                <Label data-testid="admin-employee-task-address-label">{t('portalUi.admin.employeeTasks.form.address', 'Adrese')}</Label>
                <Input value={formData.property_address} onChange={(e) => setFormData((prev) => ({ ...prev, property_address: e.target.value }))} data-testid="admin-employee-task-address" />
              </div>
            </div>
            <div className="space-y-2">
              <Label data-testid="admin-employee-task-description-label">{t('portalUi.admin.employeeTasks.form.description', 'Apraksts')}</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} data-testid="admin-employee-task-description" />
            </div>
            <div className="space-y-2">
              <Label data-testid="admin-employee-task-admin-notes-label">{t('portalUi.admin.employeeTasks.form.adminNotes', 'Administratora norādījumi darbiniekam')}</Label>
              <Textarea value={formData.admin_notes} onChange={(e) => setFormData((prev) => ({ ...prev, admin_notes: e.target.value }))} placeholder="Piem.: sākt no ziemeļu robežas, stigas ik pēc 50 m" data-testid="admin-employee-task-admin-notes" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label data-testid="admin-employee-task-status-label">{t('portalUi.admin.employeeTasks.form.status', 'Statuss')}</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}>
                  <SelectTrigger data-testid="admin-employee-task-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value} data-testid={`admin-employee-task-status-${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              {editingTask && (
                <Button variant="outline" type="button" onClick={() => openLink(editingTask)} className="mr-auto" data-testid="admin-employee-task-modal-link">
                  <Link2 className="w-4 h-4 mr-1" /> {t('portalUi.admin.employeeTasks.generateLink', 'Saite darbiniekam')}
                </Button>
              )}
              <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)} data-testid="admin-employee-task-cancel">
                {t('portalUi.admin.employeeTasks.cancel', 'Atcelt')}
              </Button>
              <Button type="submit" data-testid="admin-employee-task-save">
                {editingTask ? t('portalUi.admin.employeeTasks.save', 'Saglabāt') : t('portalUi.admin.employeeTasks.create', 'Izveidot')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <TaskLinkDialog
        task={linkTask}
        url={linkUrl}
        onClose={() => setLinkTask(null)}
        defaultEmail={linkEmail}
        justCreated={linkJustCreated}
        onSendEmail={(email, message) => sendTaskLink(linkTask.id, { email, message })}
      />

      <Dialog open={isReportsOpen} onOpenChange={setIsReportsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="admin-employee-reports-title">
              {t('portalUi.admin.employeeTasks.reportsTitle', 'Darbinieka atskaites')}
            </DialogTitle>
            <DialogDescription className="sr-only" data-testid="admin-employee-reports-desc">
              {t('portalUi.admin.employeeTasks.reportsDescription', 'Pārskatiet iesniegtās atskaites un izdevumus')}
            </DialogDescription>
          </DialogHeader>

          {selectedTask && (
            <div className="mb-4 space-y-2" data-testid="admin-employee-reports-task">
              <div className="text-sm text-[#2E2E2E]/70">{selectedTask.title}</div>
            </div>
          )}

          {reports.length === 0 ? (
            <div className="text-center text-[#2E2E2E]/60" data-testid="admin-employee-reports-empty">
              {t('portalUi.admin.employeeTasks.reportsEmpty', 'Atskaites vēl nav iesniegtas.')}
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <Card key={report.id} className="border border-[#1F3D2B]/10" data-testid={`admin-employee-report-${report.id}`}>
                  <CardHeader>
                    <CardTitle className="text-base">{report.employee_name || report.employee_id}</CardTitle>
                    <div className="text-xs text-[#2E2E2E]/60">{new Date(report.created_at).toLocaleString('lv-LV')}</div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-[#2E2E2E]/80" data-testid={`admin-employee-report-content-${report.id}`}>{report.content}</p>
                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-[#1F3D2B]">{t('portalUi.admin.employeeTasks.expenses', 'Izdevumi')}</div>
                      {report.expenses?.length ? (
                        report.expenses.map((expense) => (
                          <div key={expense.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border rounded-lg p-3" data-testid={`admin-employee-expense-${expense.id}`}>
                            <div>
                              <div className="text-sm font-medium">{expense.category} — €{expense.amount}</div>
                              {expense.note && (
                                <div className="text-xs text-[#2E2E2E]/60" data-testid={`admin-employee-expense-note-${expense.id}`}>{expense.note}</div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {expense.receipt_id && (
                                <Button size="sm" variant="outline" onClick={() => handleReceiptView(expense.receipt_id)} data-testid={`admin-employee-expense-view-${expense.id}`}>
                                  <Eye className="w-4 h-4 mr-1" />
                                  {t('portalUi.admin.employeeTasks.viewReceipt', 'Skatīt čeku')}
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => handleExpenseDelete(report.id, expense.id)} data-testid={`admin-employee-expense-delete-${expense.id}`}>
                                <Trash2 className="w-4 h-4 mr-1" />
                                {t('portalUi.admin.employeeTasks.deleteExpense', 'Dzēst izdevumu')}
                              </Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-[#2E2E2E]/60" data-testid={`admin-employee-expense-empty-${report.id}`}>
                          {t('portalUi.admin.employeeTasks.expensesEmpty', 'Izdevumu nav.')}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeTasksManagement;
